import { load } from '@cashfreepayments/cashfree-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPackGrantCredits } from '../../config/creditPacks';
import type { CreditPack } from '../../config/creditPacks';
import { supabase } from '../../lib/supabase';
import { logEvent } from '../../services/loggingService';
import { appLogger } from '../../utils/appLogger';
import { formatPackPrice, formatLocaleAmount, formatZeroTax, getPackPrice } from '../../utils/localePrice';
import { trackInitiateCheckout } from '../../utils/metaPixel';
import {
  countryFromBillingName,
  getPhoneCountry,
  parseStoredPhone,
  stripNationalDigits,
  toCashfreeCustomerPhone,
  toE164,
} from '../../utils/phoneCountries';
import {
  getCashfreeSdkMode,
  isValidIndianPhone,
  mapCashfreePaymentError,
  mapSubscriptionCreateError,
  postCashfreeOrderCheckout,
  postCashfreeSubscriptionCheckout,
  interpretCashfreeCheckoutResult,
  cashfreeFailureMessage,
  verifyCashfreeOrderStatus,
  resolvePackFromId,
  stashCheckoutActivePlan,
  shouldUseCashfreeHostedPaymentPage,
  shouldUseCashfreeFullPageCheckout,
  validateCashfreeOrderSessionEnv,
  validateCashfreeSubscriptionSessionEnv,
} from './creditCheckoutUtils';
import { recordSubscriptionPaymentFailed } from '../../services/packSubscriptionService';
import { getAppOrigin } from '../../shared/seo/siteOrigin';
import { creditCheckoutUrl, goToPaymentSuccess } from '../../utils/creditCheckoutNavigation';
import { bootstrapPaymentReturn, stashPaymentReturnTarget, stashSessionForPaymentRedirect } from '../../utils/paymentReturnBootstrap';

type CashfreeSdk = Awaited<ReturnType<typeof load>>;

const CASHFREE_SDK_MODE = getCashfreeSdkMode();

export type CreditsCheckoutStep = 'checkout' | 'processing';

type UseCreditsCheckoutOptions = {
  initialPackId?: string;
  onPaymentSuccess?: (orderId: string, credits: number) => void;
};

export function useCreditsCheckout({ initialPackId, onPaymentSuccess }: UseCreditsCheckoutOptions) {
  const { i18n, t } = useTranslation();
  const [step, setStep] = useState<CreditsCheckoutStep>('checkout');
  const [selectedPlan, setSelectedPlan] = useState<CreditPack | null>(null);
  const [loading, setLoading] = useState(false);
  const [paymentFailure, setPaymentFailure] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [enableAutoRenew, setEnableAutoRenewState] = useState(true);
  const [cardholderName, setCardholderName] = useState('');
  const activeSubscriptionIdRef = useRef<string | null>(null);
  const activeOrderIdRef = useRef<string | null>(null);

  const [userData, setUserData] = useState({
    email: '',
    phone: '',
    phoneCountry: 'IN',
    firstName: '',
    lastName: '',
    billingAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  });

  const isIndianCustomer = /^(india|in)$/i.test(userData.country.trim());

  const calcLocaleTotals = useCallback((plan: CreditPack) => {
    const localePrice = getPackPrice(plan.id, i18n.language);
    const gstRatePct = localePrice.currency === 'INR' && isIndianCustomer ? 18 : 0;
    const gst = parseFloat((localePrice.amount * gstRatePct / 100).toFixed(2));
    const total = parseFloat((localePrice.amount + gst).toFixed(2));
    return { localePrice, gstRatePct, gst, total, base: localePrice.amount };
  }, [i18n.language, isIndianCustomer]);

  const formatPlanDisplayPrice = useCallback(
    (plan: CreditPack) => formatPackPrice(plan.id, i18n.language),
    [i18n.language],
  );

  const formatSwitcherPlanPrice = formatPlanDisplayPrice;

  const formatCheckoutTotal = useCallback(
    (plan: CreditPack) => {
      const { total, localePrice } = calcLocaleTotals(plan);
      return formatLocaleAmount(total, localePrice);
    },
    [calcLocaleTotals],
  );

  const formatLocaleSubtotal = useCallback(
    (plan: CreditPack) => {
      const { base, localePrice } = calcLocaleTotals(plan);
      return formatLocaleAmount(base, localePrice);
    },
    [calcLocaleTotals],
  );

  const formatLocaleGst = useCallback(
    (plan: CreditPack) => {
      const { gst, localePrice } = calcLocaleTotals(plan);
      return formatLocaleAmount(gst, localePrice);
    },
    [calcLocaleTotals],
  );

  const getLocaleGstRate = useCallback(
    (plan: CreditPack) => calcLocaleTotals(plan).gstRatePct,
    [calcLocaleTotals],
  );

  const updateBilling = (field: keyof typeof userData, value: string) => {
    setUserData((prev) => {
      if (field === 'country') {
        return {
          ...prev,
          country: value,
          phoneCountry: countryFromBillingName(value),
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const updateCountry = (countryName: string, iso2: string) => {
    setUserData((prev) => ({
      ...prev,
      country: countryName,
      phoneCountry: iso2.toUpperCase(),
    }));
  };

  const updatePhoneCountry = (iso2: string) => {
    setUserData((prev) => ({
      ...prev,
      phoneCountry: iso2.toUpperCase(),
    }));
  };

  const updatePhoneNational = (digits: string) => {
    setUserData((prev) => {
      const country = getPhoneCountry(prev.phoneCountry);
      return {
        ...prev,
        phone: stripNationalDigits(digits, country.nationalLength),
      };
    });
  };

  const persistCheckoutProfile = async (data: typeof userData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Authentication required. Please sign in to continue.');

    const fullName = [data.firstName, data.lastName]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(' ');

    const e164Phone = toE164(data.phoneCountry, data.phone) || null;

    const { error } = await supabase.from('users').update({
      phone: e164Phone,
      ...(fullName ? { name: fullName } : {}),
      street_address: data.billingAddress.trim() || null,
      city: data.city.trim() || null,
      state: data.state.trim() || null,
      zip_code: data.zipCode.trim() || null,
      country: data.country.trim() || null,
      updated_at: new Date().toISOString(),
    }).eq('user_id', user.id);

    if (error) {
      appLogger.warn('Checkout profile save failed', error);

      // Detect duplicate phone number constraint violation
      const isDuplicatePhone =
        error.code === '23505' &&
        (error.message?.includes('idx_users_phone_unique') || error.message?.includes('phone'));

      if (isDuplicatePhone) {
        throw new Error(t('creditsPurchaseModal.phoneAlreadyInUse', {
          defaultValue: 'This phone number is already registered with another account. Please use a different mobile number.',
        }));
      }

      // Detect other common constraint violations
      if (error.code === '23505') {
        throw new Error(t('creditsPurchaseModal.duplicateDetail', {
          defaultValue: 'One of your details is already linked to another account. Please check and try again.',
        }));
      }

      throw new Error(t('creditsPurchaseModal.profileSaveFailed', {
        defaultValue: 'Could not save your details. Please try again.',
      }));
    }
  };

  const applyCardholderToBilling = () => {
    if (!cardholderName.trim()) return;
    const parts = cardholderName.trim().split(/\s+/);
    setUserData((prev) => ({
      ...prev,
      firstName: parts[0] || prev.firstName,
      lastName: parts.slice(1).join(' ') || prev.lastName,
    }));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pack = resolvePackFromId(initialPackId) ??
        resolvePackFromId('pack-monthly');
      if (pack) {
        setSelectedPlan(pack);
        trackInitiateCheckout(pack.price, 'INR');
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: userProfile } = await supabase
          .from('users')
          .select('name, phone, street_address, city, state, zip_code, country')
          .eq('user_id', user.id)
          .single();

        const fullName = userProfile?.name || user.user_metadata?.name || user.user_metadata?.full_name || user.email?.split('@')[0] || '';
        const nameParts = (fullName || '').trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        const profileCountry = userProfile?.country || '';
        const phoneCountryIso = countryFromBillingName(profileCountry);
        const storedPhone = userProfile?.phone || (user.phone as string) || '';
        const parsedPhone = parseStoredPhone(storedPhone, phoneCountryIso);

        if (!cancelled) {
          setUserData({
            email: user.email || '',
            phone: parsedPhone.nationalDigits,
            phoneCountry: parsedPhone.countryIso2,
            firstName,
            lastName,
            billingAddress: userProfile?.street_address || '',
            city: userProfile?.city || '',
            state: userProfile?.state || '',
            zipCode: userProfile?.zip_code || '',
            country: profileCountry,
          });
          setCardholderName([firstName, lastName].filter(Boolean).join(' '));
        }
      } catch (err) {
        appLogger.error('Checkout user prefetch failed', err);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [initialPackId]);

  const setEnableAutoRenew = useCallback((enabled: boolean) => {
    setEnableAutoRenewState(enabled);
    setPaymentFailure(null);
  }, []);

  const selectPlan = useCallback((packId: string) => {
    const pack = resolvePackFromId(packId);
    if (!pack) return;
    setSelectedPlan(pack);
    setEnableAutoRenewState(true);
    setPaymentFailure(null);
    const url = creditCheckoutUrl(pack.id);
    window.history.replaceState({}, '', url);
    trackInitiateCheckout(pack.price, 'INR');
  }, []);

  const dismissPaymentFailure = () => setPaymentFailure(null);

  const recordCheckoutFailure = async (message: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      if (activeSubscriptionIdRef.current) {
        await recordSubscriptionPaymentFailed(activeSubscriptionIdRef.current, message);
        return;
      }

      if (activeOrderIdRef.current) {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-cashfree-payment`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            order_id: activeOrderIdRef.current,
            mark_failed: true,
            reason: message,
          }),
        });
      }
    } catch (err) {
      appLogger.warn('Failed to record payment attempt', err);
    }
  };

  const failCheckout = (message: string) => {
    setPaymentFailure(message);
    setStep('checkout');
    setLoading(false);
    try { localStorage.removeItem('payment_in_progress'); } catch (_) {}
    void recordCheckoutFailure(message);
  };

  const getSessionToken = async () => {
    const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
    if (refreshErr) appLogger.debug('Session refresh warning (non-fatal):', refreshErr.message);
    const session = refreshed?.session ?? (await supabase.auth.getSession()).data.session;
    if (!session?.access_token) {
      throw new Error('Authentication required. Please sign in to continue.');
    }
    return session;
  };

  const buildCheckoutPayload = (accessToken: string) => ({
    amount: selectedPlan!.price,
    customerEmail: userData.email,
    customerPhone: toCashfreeCustomerPhone(userData.phoneCountry, userData.phone),
    planId: selectedPlan!.id,
    credits: getPackGrantCredits(selectedPlan!),
    locale: i18n.language || 'en',
    country: userData.country || '',
    returnUrl: typeof window !== 'undefined'
      ? `${getAppOrigin()}/payment-success`
      : undefined,
    billing: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      address: userData.billingAddress,
      city: userData.city,
      state: userData.state,
      zipCode: userData.zipCode,
      country: userData.country,
    },
    access_token: accessToken,
  });

  const processRecurringSubscription = async () => {
    if (!selectedPlan) return;

    setStep('processing');
    setLoading(true);
    setPaymentFailure(null);

    try {
      const session = await getSessionToken();
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-cashfree-subscription`;
      const rawResp = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildCheckoutPayload(session.access_token)),
      });

      if (!rawResp.ok) {
        let errMsg = 'Failed to create subscription';
        try {
          const e = await rawResp.json();
          errMsg = mapSubscriptionCreateError(e)
            || e.error
            || e.details?.message
            || e.details?.reason
            || (typeof e.details === 'string' ? e.details : errMsg);
        } catch (_) {}
        throw new Error(errMsg);
      }

      const data = await rawResp.json();
      const envMismatch = validateCashfreeSubscriptionSessionEnv(data);
      if (envMismatch) throw new Error(envMismatch);

      const subsSessionId = String(data.subscription_session_id || '').trim();
      const subscriptionId = String(data.subscription_id || '').trim();
      if (!subsSessionId || !subscriptionId) {
        throw new Error('Subscription session not received from payment gateway.');
      }

      activeSubscriptionIdRef.current = subscriptionId;
      activeOrderIdRef.current = null;
      stashCheckoutActivePlan(selectedPlan.id);
      try { localStorage.setItem('payment_in_progress', 'true'); } catch (_) {}
      stashPaymentReturnTarget({ type: 'recurring', subscriptionId });
      // Stash refresh_token in a cookie so session survives mobile UPI app switches
      try {
        const { data: { session: currentSess } } = await supabase.auth.getSession();
        if (currentSess?.refresh_token) stashSessionForPaymentRedirect(currentSess.refresh_token);
      } catch { /* best-effort */ }
      bootstrapPaymentReturn();

      logEvent({
        level: 'info',
        action: 'subscription_checkout_redirect',
        details: {
          subscriptionId,
          planId: selectedPlan.id,
          cashfree_env: data.cashfree_env,
          sdk_mode: CASHFREE_SDK_MODE,
        },
      });

      // Hosted POST checkout (matches Cashfree SDK fields; avoids SDK mode/context issues).
      postCashfreeSubscriptionCheckout(subsSessionId, CASHFREE_SDK_MODE);
      return;
    } catch (err) {
      appLogger.error('Subscription checkout error', err);
      failCheckout(mapCashfreePaymentError(
        err instanceof Error ? err.message : 'Subscription setup failed',
      ));
    } finally {
      setLoading(false);
    }
  };

  const processOneTimePayment = async () => {
    if (!selectedPlan) return;

    setStep('processing');
    setLoading(true);
    setPaymentFailure(null);

    try {
      const session = await getSessionToken();
      const paymentData = buildCheckoutPayload(session.access_token);

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-cashfree-order`;
      const rawResp = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      if (!rawResp.ok) {
        let errMsg = 'Failed to create payment order';
        try { const e = await rawResp.json(); errMsg = e.error || e.details || errMsg; } catch (_) {}
        throw new Error(errMsg);
      }

      const data = await rawResp.json();
      const envMismatch = validateCashfreeOrderSessionEnv(data);
      if (envMismatch) throw new Error(envMismatch);

      const sessionId = (data.payment_session_id || data.session_id || '').trim();
      const orderId = (data.order_id || data.cf_order_id || '').trim();
      if (!sessionId) throw new Error('Payment session ID not received from gateway.');
      if (!orderId) throw new Error('Order ID not received from gateway.');

      activeOrderIdRef.current = orderId;
      activeSubscriptionIdRef.current = null;
      stashCheckoutActivePlan(selectedPlan.id);
      try { localStorage.setItem('payment_in_progress', 'true'); } catch (_) {}
      stashPaymentReturnTarget({ type: 'one_time', orderId });
      // Stash refresh_token in a cookie so session survives mobile UPI app switches
      try {
        const { data: { session: currentSess } } = await supabase.auth.getSession();
        if (currentSess?.refresh_token) stashSessionForPaymentRedirect(currentSess.refresh_token);
      } catch { /* best-effort */ }
      bootstrapPaymentReturn();

      // localhost + PROD: full-page hosted checkout (modal postMessages fail cross-origin).
      if (shouldUseCashfreeFullPageCheckout()) {
        logEvent({
          level: 'info',
          action: 'payment_checkout_hosted_redirect',
          details: { orderId, planId: selectedPlan.id, reason: 'full_page_checkout' },
        });
        setStep('checkout');
        postCashfreeOrderCheckout(sessionId, CASHFREE_SDK_MODE);
        return;
      }

      const cfInstance = await load({ mode: CASHFREE_SDK_MODE });

      // Cashfree UI takes over — dismiss our processing overlay first
      setStep('checkout');
      setLoading(false);

      let result: unknown;
      try {
        result = await cfInstance.checkout({
          paymentSessionId: sessionId,
          redirectTarget: '_modal',
        });
      } catch (checkoutErr) {
        appLogger.error('Cashfree modal checkout error', checkoutErr);
        failCheckout(mapCashfreePaymentError(
          checkoutErr instanceof Error ? checkoutErr.message : 'Payment could not be opened',
        ));
        return;
      }

      const outcome = interpretCashfreeCheckoutResult(result);
      if (outcome === 'success') {
        logEvent({
          level: 'info',
          action: 'payment_checkout_completed_redirect',
          details: { orderId, amount: selectedPlan.price },
        });
        if (onPaymentSuccess) {
          onPaymentSuccess(orderId, getPackGrantCredits(selectedPlan));
        } else {
          goToPaymentSuccess(orderId);
        }
        return;
      }

      if (outcome === 'failed') {
        const errMsg = cashfreeFailureMessage(result);
        failCheckout(errMsg);
        logEvent({
          level: 'warn',
          action: 'payment_checkout_failed',
          details: { orderId, planId: selectedPlan.id, error: errMsg, outcome },
        });
        return;
      }

      // Cancelled or modal closed without success payload — confirm with Cashfree before failing
      const serverStatus = await verifyCashfreeOrderStatus(orderId, session.access_token);
      if (serverStatus === 'completed') {
        if (onPaymentSuccess) {
          onPaymentSuccess(orderId, getPackGrantCredits(selectedPlan));
        } else {
          goToPaymentSuccess(orderId);
        }
        return;
      }

      if (serverStatus === 'pending') {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        const retryStatus = await verifyCashfreeOrderStatus(orderId, session.access_token);
        if (retryStatus === 'completed') {
          if (onPaymentSuccess) {
            onPaymentSuccess(orderId, getPackGrantCredits(selectedPlan));
          } else {
            goToPaymentSuccess(orderId);
          }
          return;
        }
        if (retryStatus === 'pending') {
          // Rare in-flight UPI in modal — allow /payment-success to poll
          goToPaymentSuccess(orderId);
          return;
        }
      }

      const cancelMsg = cashfreeFailureMessage(result);
      failCheckout(cancelMsg);
      logEvent({
        level: 'warn',
        action: 'payment_checkout_cancelled',
        details: { orderId, planId: selectedPlan.id, outcome, serverStatus },
      });
      return;
    } catch (err) {
      appLogger.error('Payment processing error', err);
      const message = mapCashfreePaymentError(
        err instanceof Error ? err.message : 'Payment processing failed',
      );
      failCheckout(message);
      logEvent({
        level: 'error',
        action: 'payment_processing_error',
        details: {
          planId: selectedPlan?.id,
          error: err instanceof Error ? err.message : String(err),
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const useSubscriptionCheckout = Boolean(selectedPlan?.autoRenew && enableAutoRenew);

  const processPayment = () => {
    if (!selectedPlan) return;
    if (useSubscriptionCheckout) {
      processRecurringSubscription();
    } else {
      processOneTimePayment();
    }
  };

  const handlePayAndUpgrade = async () => {
    if (!selectedPlan || !userData.email.trim()) return;
    setPaymentFailure(null);

    const phone = toCashfreeCustomerPhone(userData.phoneCountry, userData.phone);
    if (useSubscriptionCheckout) {
      if (userData.phoneCountry !== 'IN') {
        setPaymentFailure(t('creditsPurchaseModal.mobileNumberIndiaRequired', {
          defaultValue: 'INR subscriptions require an Indian (+91) mobile number.',
        }));
        return;
      }
      if (!isValidIndianPhone(phone)) {
        setPaymentFailure(t('creditsPurchaseModal.mobileNumberInvalid', {
          defaultValue: 'Enter a valid 10-digit Indian mobile number below.',
        }));
        return;
      }
      if (!userData.firstName.trim() || !userData.lastName.trim()) {
        setPaymentFailure(t('creditsPurchaseModal.nameRequired', {
          defaultValue: 'Enter your first and last name below.',
        }));
        return;
      }
    } else if (!isValidIndianPhone(phone)) {
      setPaymentFailure(t('creditsPurchaseModal.mobileNumberInvalid', {
        defaultValue: 'Enter a valid 10-digit Indian mobile number below.',
      }));
      return;
    }

    applyCardholderToBilling();

    const profilePayload = { ...userData, phone };
    if (phone !== userData.phone) {
      setUserData((prev) => ({ ...prev, phone }));
    }

    try {
      await persistCheckoutProfile(profilePayload);
    } catch (err) {
      setPaymentFailure(err instanceof Error ? err.message : t('creditsPurchaseModal.profileSaveFailed', {
        defaultValue: 'Could not save your details. Please try again.',
      }));
      return;
    }

    processPayment();
    logEvent({
      level: 'info',
      action: useSubscriptionCheckout ? 'subscribe_clicked' : 'pay_and_upgrade_clicked',
      details: { planId: selectedPlan.id, enableAutoRenew: useSubscriptionCheckout },
    });
  };

  return {
    ready,
    step,
    setStep,
    selectedPlan,
    loading,
    paymentFailure,
    dismissPaymentFailure,
    billingOpen,
    setBillingOpen,
    userData,
    updateBilling,
    updateCountry,
    updatePhoneCountry,
    updatePhoneNational,
    getLocaleGstRate,
    formatPlanDisplayPrice,
    formatSwitcherPlanPrice,
    formatCheckoutTotal,
    formatLocaleSubtotal,
    formatLocaleGst,
    formatZeroTax: () => formatZeroTax(i18n.language),
    handlePayAndUpgrade,
    selectPlan,
    enableAutoRenew,
    setEnableAutoRenew,
    useSubscriptionCheckout,
  };
}
